import React from "react";
import { Info } from "lucide-react";

/**
 * IllustrativeDisclaimer
 *
 * The Deal Structure content on this site is educational: it walks through
 * how a working commercial finance broker would approach a common file, not
 * specific transactions closed by Steady Path Funding. This component is the
 * plain-English notice that appears on every Deal Structure page and in the
 * related teaser inside blog articles.
 *
 * Two visual variants:
 *   - "banner" (default): full-width inline banner, use on detail pages + index
 *   - "inline": compact single-line variant, use inside asides/cards
 */
export const IllustrativeDisclaimer = ({ variant = "banner", className = "" }) => {
  if (variant === "inline") {
    return (
      <div
        data-testid="illustrative-disclaimer-inline"
        className={`flex items-start gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[color:var(--bone)]/50 ${className}`}
      >
        <Info size={11} className="mt-0.5 flex-shrink-0 text-[color:var(--gold)]" />
        <span>Illustrative deal structure · Not a specific closed transaction</span>
      </div>
    );
  }

  return (
    <div
      data-testid="illustrative-disclaimer-banner"
      className={`border border-[color:var(--gold)]/40 bg-[color:var(--bg-elev)] px-5 py-4 flex items-start gap-3 ${className}`}
    >
      <Info size={16} className="mt-0.5 flex-shrink-0 text-[color:var(--gold)]" />
      <div>
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
          Illustrative deal structure
        </div>
        <p className="mt-2 text-sm text-[color:var(--bone)]/75 leading-relaxed">
          The scenario and numbers below are an illustrative example of how a
          working commercial finance broker approaches this type of file — not
          a specific transaction closed by Steady Path Funding. Every industry,
          location, and identifying detail is fictionalized. Actual terms
          depend on your credit, revenue, and lender fit.
        </p>
      </div>
    </div>
  );
};

export default IllustrativeDisclaimer;
